import React, { forwardRef, useEffect, useImperativeHandle } from 'react';

import { ajvResolver } from '@hookform/resolvers/ajv';
import {
	Checkbox,
	Divider,
	Stack,
	Table,
	TableBody,
	TableCell,
	tableCellClasses,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	useTheme,
} from '@mui/material';
import { JSONSchemaType } from 'ajv';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWRImmutable from 'swr/immutable';

import { FormContainer } from '~components';
import { useFormContainerState } from '~hooks';
import { FormWrapperRefProps } from '~interfaces/refProps';
import { datetimeNoSecondsFormatter } from '~utils/dateUtils';

import schema from './cancelOneOffPaymentsSchema.json';
import PaymentStatusEnum from '../../enums/paymentStatusEnum';
import OneOffPaymentsService from '../../services/oneOffPaymentsService';

const service = new OneOffPaymentsService();

interface CanceledPaymentData {
	oneOffPaymentId: string;
	total: number;
	vatPercentage: number;
}

interface CancelOneOffPaymentsFormProps {
	bookingId: string;
	onSubmit?: (value: CanceledPaymentData[]) => void;
	totalBookingAmount: number;
}

const CancelOneOffPaymentsForm = forwardRef<FormWrapperRefProps, CancelOneOffPaymentsFormProps>(
	({ bookingId, onSubmit, totalBookingAmount }, ref) => {
		const { t } = useTranslation();
		const theme = useTheme();

		const { data: oneOffPaymentsData, isLoading: isOneOffPaymentsLoading } = useSWRImmutable(
			bookingId ?
				[service.basePath, { bookingId: bookingId, status: PaymentStatusEnum.Pending }]
			:	null,
			([_, args]) => service.getOneOffPayments(args),
			{
				revalidateOnMount: true,
			}
		);

		const { setDisabled } = useFormContainerState();

		const { getValues, control, formState, watch } = useForm<{
			canceledPayments: CanceledPaymentData[];
		}>({
			defaultValues: { canceledPayments: [] },
			mode: 'onChange',
			resolver: ajvResolver(schema as JSONSchemaType<any>),
		});
		const { append, remove } = useFieldArray({
			control: control,
			name: 'canceledPayments',
		});

		useImperativeHandle(
			ref,
			() => ({
				onSubmit: () => onSubmit?.(getValues().canceledPayments),
			}),
			[onSubmit, getValues]
		);

		useEffect(() => {
			setDisabled?.(!formState.isValid);
		}, [formState.isValid, setDisabled]);

		const watchCanceledPayments = watch('canceledPayments');

		// TODO: in useMemo for better performance?
		const totalCanceledPaymentAmount = watchCanceledPayments.reduce(
			(acc, payment) => (acc += payment.total),
			0
		);
		const newTotal = totalBookingAmount - totalCanceledPaymentAmount;

		return (
			<FormContainer loading={isOneOffPaymentsLoading}>
				<Typography variant='body2'>
					{t('views.bookings.bookingDetails.cancelPaymentDialog.description')}
				</Typography>
				<TableContainer>
					<Table
						sx={{
							px: 0,
							[`& .${tableCellClasses.root}`]: {
								borderBottom: 'none',
								'&:first-child': {
									pl: 0,
								},
								'&:last-child': {
									pr: 0,
								},
							},
						}}
					>
						<TableHead>
							<TableRow
								sx={{
									'& .MuiTableCell-head': {
										color: theme.palette.grey[600],
										backgroundColor: 'white',
										fontSize: 12,
										py: 1,
									},
								}}
							>
								<TableCell></TableCell>
								<TableCell>{t('views.devices.heartbeatsList.lastUpdated')}</TableCell>
								<TableCell>{t('ui.amount')}</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{oneOffPaymentsData?.results.map((oneOffPayment) => {
								const canceledPaymentIndex = watchCanceledPayments.findIndex(
									(el) => el.oneOffPaymentId === oneOffPayment.id
								);
								const canceledPayment = watchCanceledPayments.find(
									(el) => el.oneOffPaymentId === oneOffPayment.id
								);

								return (
									<TableRow>
										<TableCell padding='checkbox'>
											<Checkbox
												value={canceledPayment != null}
												onChange={(_, checked) =>
													checked ?
														append({
															oneOffPaymentId: oneOffPayment.id,
															total: oneOffPayment.refundableTotal,
															vatPercentage: oneOffPayment.vatPercentage,
														})
													: canceledPaymentIndex >= 0 ? remove(canceledPaymentIndex)
													: null
												}
											/>
										</TableCell>
										<TableCell>
											{datetimeNoSecondsFormatter.format(oneOffPayment.datePaid)}
										</TableCell>
										<TableCell>{`€${oneOffPayment.refundableTotal.toFixed(2)}`}</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>

				<Stack spacing={1}>
					<Stack direction='row' sx={{ justifyContent: 'space-between' }}>
						<Typography variant='body2'>{`${t('ui.label.currentTotal')} (${t('ui.inclVat')})`}</Typography>
						<Typography variant='body2'>{`€${totalBookingAmount.toFixed(2)}`}</Typography>
					</Stack>
					<Stack direction='row' sx={{ justifyContent: 'space-between' }}>
						<Typography variant='body2'>{`${t('canceledTotal')} (${t('ui.inclVat')})`}</Typography>
						<Typography variant='body2'>{`€${totalCanceledPaymentAmount.toFixed(2)}`}</Typography>
					</Stack>
					<Divider />
					<Stack direction='row' sx={{ justifyContent: 'space-between' }}>
						<Typography variant='body2'>{`${t('ui.label.newTotal')} (${t('ui.inclVat')})`}</Typography>
						<Typography variant='body2'>{`€${newTotal.toFixed(2)}`}</Typography>
					</Stack>
				</Stack>
			</FormContainer>
		);
	}
);

export default CancelOneOffPaymentsForm;
